import React, { useRef, useState, useEffect, useCallback } from "react";
import {
  Box,
  Pagination,
  Typography,
} from "@mui/material";
import {
  MaterialReactTable,
  MRT_ColumnDef,
  MRT_SortingState,
  MRT_ColumnFiltersState,
} from "material-react-table";

type DataTableFiveProps<T extends Record<string, any>> = {
  data: T[];
  total: number;

  page: number; // 1-based
  pageSize: number;

  onPageChange: (page: number) => void;

  columns: MRT_ColumnDef<T>[];
  getRowId?: (row: T) => string;

  isLoading?: boolean;
  isFetching?: boolean;
  isError?: boolean;

  onRowClick?: (row: { original: T }) => void;
  getRowTextColor?: (row: T) => string;

  // Server-side sorting
  sorting?: MRT_SortingState;
  onSortingChange?: (sorting: MRT_SortingState) => void;

  // Server-side filtering
  columnFilters?: MRT_ColumnFiltersState;
  onColumnFiltersChange?: (filters: MRT_ColumnFiltersState) => void;

  emptyMessage?: string;
};

export function DataTableFive<T extends Record<string, any>>({
  data,
  total,
  page,
  pageSize,
  onPageChange,
  columns,
  getRowId,
  isLoading = false,
  isFetching = false,
  isError,
  onRowClick,
  sorting,
  onSortingChange,
  columnFilters,
  onColumnFiltersChange,
  getRowTextColor,
  emptyMessage = "No records found.",
}: DataTableFiveProps<T>) {
  const isSortingEnabled = Boolean(onSortingChange);
  const isFilteringEnabled = Boolean(onColumnFiltersChange);
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // Top horizontal scroll synchronization
  const topScrollRef = useRef<HTMLDivElement>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [scrollWidth, setScrollWidth] = useState(0);
  const [clientWidth, setClientWidth] = useState(0);
  const isSyncingRef = useRef<"top" | "table" | null>(null);

  const measureScroll = useCallback(() => {
    const container = tableContainerRef.current;
    if (!container) return;

    const tableEl = container.querySelector("table");
    const sWidth = Math.max(container.scrollWidth, tableEl ? tableEl.scrollWidth : 0);
    const cWidth = container.clientWidth;

    setScrollWidth(sWidth);
    setClientWidth(cWidth);

    if (topScrollRef.current) {
      topScrollRef.current.scrollLeft = container.scrollLeft;
    }
  }, []);

  useEffect(() => {
    measureScroll();

    const container = tableContainerRef.current;
    if (!container) return;

    const observer = new ResizeObserver(() => {
      measureScroll();
    });
    observer.observe(container);

    const tableEl = container.querySelector("table");
    if (tableEl) {
      observer.observe(tableEl);
    }

    window.addEventListener("resize", measureScroll);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measureScroll);
    };
  }, [data, columns, isLoading, measureScroll]);

  const handleTopScroll = () => {
    if (isSyncingRef.current === "table") return;
    isSyncingRef.current = "top";
    if (topScrollRef.current && tableContainerRef.current) {
      tableContainerRef.current.scrollLeft = topScrollRef.current.scrollLeft;
    }
    requestAnimationFrame(() => {
      if (isSyncingRef.current === "top") isSyncingRef.current = null;
    });
  };

  const handleTableScroll = () => {
    if (isSyncingRef.current === "top") return;
    isSyncingRef.current = "table";
    if (topScrollRef.current && tableContainerRef.current) {
      topScrollRef.current.scrollLeft = tableContainerRef.current.scrollLeft;
    }
    requestAnimationFrame(() => {
      if (isSyncingRef.current === "table") isSyncingRef.current = null;
    });
  };

  const hasHorizontalScroll = scrollWidth > clientWidth && clientWidth > 0;

  return (
    <Box width="100%" display="flex" flexDirection="column">
      {/* Top Horizontal Scrollbar */}
      {hasHorizontalScroll && (
        <Box
          sx={{
            width: "100%",
            backgroundColor: "#F8FAFC",
            border: "1px solid #E2E8F0",
            borderBottom: "1px solid #EDF2F7",
            borderTopLeftRadius: "6px",
            borderTopRightRadius: "6px",
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            px: 0.5,
            py: 0.5,
          }}
        >
          <Box
            ref={topScrollRef}
            onScroll={handleTopScroll}
            sx={{
              width: "100%",
              overflowX: "auto",
              overflowY: "hidden",
              height: "10px",
              scrollbarWidth: "thin",
              scrollbarColor: "#94A3B8 #E2E8F0",
              "&::-webkit-scrollbar": {
                height: "6px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "#E2E8F0",
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#94A3B8",
                borderRadius: "4px",
                "&:hover": {
                  backgroundColor: "#64748B",
                },
              },
            }}
          >
            <Box sx={{ width: `${scrollWidth}px`, height: "1px" }} />
          </Box>
        </Box>
      )}

      <MaterialReactTable
        columns={columns}
        data={data}
        getRowId={(row, index) => {
          try {
            return getRowId ? getRowId(row) : `row-${index}`;
          } catch {
            return `placeholder-${index}`;
          }
        }}
        enablePagination={false}
        enableBottomToolbar={false}
        enableGlobalFilter={false}
        enableColumnFilters={isFilteringEnabled}
        manualFiltering={isFilteringEnabled}
        enableSorting={isSortingEnabled}
        manualSorting={isSortingEnabled}
        enableColumnActions={false}
        enableDensityToggle={false}
        enableFullScreenToggle={false}
        enableHiding={false}
        enableTopToolbar={false}
        initialState={{
          density: "compact",
        }}
        state={{
          sorting: sorting ?? [],
          columnFilters: columnFilters ?? [],
          showSkeletons: isLoading,
          showLoadingOverlay: false,
          showProgressBars: isFetching && !isLoading,
        }}
        muiSkeletonProps={{
          animation: "wave",
          height: 24,
        }}
        muiLinearProgressProps={{
          sx: {
            "& .MuiLinearProgress-bar": {
              backgroundColor: "#0072BC",
            },
          },
        }}
        muiTablePaperProps={{
          elevation: 0,
          sx: {
            border: "1px solid #E2E8F0",
            borderRadius: hasHorizontalScroll ? "0 0 6px 6px" : "6px",
            borderTop: hasHorizontalScroll ? "none" : "1px solid #E2E8F0",
            overflow: "hidden",
            backgroundColor: "#FFFFFF",
          },
        }}
        muiTableContainerProps={{
          ref: tableContainerRef,
          onScroll: handleTableScroll,
          sx: {
            maxWidth: "100%",
            overflowX: "auto",
            scrollbarWidth: "thin",
            scrollbarColor: "#94A3B8 #F1F5F9",
            "&::-webkit-scrollbar": {
              height: "6px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "#F1F5F9",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#CBD5E1",
              borderRadius: "4px",
              "&:hover": {
                backgroundColor: "#94A3B8",
              },
            },
          },
        }}
        muiTableHeadCellProps={{
          sx: {
            backgroundColor: "#F8FAFC",
            borderBottom: "1px solid #E2E8F0",
            color: "#334155",
            fontSize: "12px",
            fontWeight: 700,
            letterSpacing: "0.03em",
            py: 1.25,
            textTransform: "uppercase",
          },
        }}
        muiTableBodyCellProps={{
          sx: {
            borderBottom: "1px solid #EDF1F5",
            color: "#1E293B",
            fontSize: "13px",
            fontWeight: 400,
            py: 1.25,
          },
        }}
        muiTableBodyRowProps={({ row }) => ({
          onClick: () => onRowClick?.(row),
          sx: {
            cursor: onRowClick ? "pointer" : "default",
            color: getRowTextColor ? getRowTextColor(row.original) : "inherit",
            transition: "background-color 0.15s ease",
            "&:hover": {
              backgroundColor: "#F1F5F9 !important",
            },
          },
        })}
        renderEmptyRowsFallback={() => (
          <Typography
            sx={{
              color: "text.secondary",
              fontSize: 13.5,
              py: 6,
              textAlign: "center",
              width: "100%",
            }}
          >
            {emptyMessage}
          </Typography>
        )}
        onSortingChange={(updater) => {
          if (!onSortingChange) return;
          const newSorting =
            typeof updater === "function" ? updater(sorting ?? []) : updater;
          onSortingChange(newSorting);
        }}
        onColumnFiltersChange={(updater) => {
          if (!onColumnFiltersChange) return;
          const newFilters =
            typeof updater === "function" ? updater(columnFilters ?? []) : updater;
          onColumnFiltersChange(newFilters);
        }}
      />

      {/* Pagination Footer */}
      {totalPages > 1 && (
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mt={2}
          px={1}
        >
          <Typography variant="caption" color="text.secondary" fontSize={12.5}>
            Showing page <strong>{page}</strong> of <strong>{totalPages}</strong> (
            {Number(total).toLocaleString()} total records)
          </Typography>

          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, newPage) => onPageChange(newPage)}
            shape="rounded"
            size="medium"
            sx={{
              "& .MuiPaginationItem-root": {
                borderRadius: "4px",
                fontSize: 13,
                fontWeight: 600,
                "&.Mui-selected": {
                  backgroundColor: "#0072BC",
                  color: "#FFFFFF",
                  "&:hover": {
                    backgroundColor: "#005a96",
                  },
                },
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
}
